import React, { useState } from 'react';
import axios from 'axios';
import { useAdminTheme } from '../context/AdminThemeContext';
import { Play, FileText, Calendar, Loader2, Zap } from 'lucide-react';

const AdminAiBlog: React.FC = () => {
  const { colors } = useAdminTheme();
  const [keywords, setKeywords] = useState('');
  const [days, setDays] = useState('1');
  const [loadingType, setLoadingType] = useState<'none' | 'scheduled' | 'instant'>('none');
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  // 1. Bulk Scheduler Function
  const handleStartAutomation = async () => {
    setLoadingType('scheduled');
    setMessage(null);
    const keywordArray = keywords.split('\n').map(k => k.trim()).filter(k => k !== '');

    if (keywordArray.length === 0) {
        setMessage({ text: 'Please enter at least one keyword.', type: 'error' });
        setLoadingType('none'); return;
    }

    try {
      const response = await axios.post('https://www.projectinfotech.in/api/admin/blog-automation/start', {
        keywords: keywordArray,
        days: parseInt(days)
      });
      setMessage({ text: `Success! ${response.data.keywordsAdded} blogs scheduled.`, type: 'success' });
      setKeywords('');
    } catch (error) {
      setMessage({ text: 'Error scheduling automation.', type: 'error' });
    } finally {
      setLoadingType('none');
    }
  };

  // 2. Instant Generator Function
  const handleInstantGenerate = async () => {
    setLoadingType('instant');
    setMessage(null);
    const firstKeyword = keywords.split('\n')[0].trim(); // Sirf pehla keyword uthayega

    if (!firstKeyword) {
        setMessage({ text: 'Please enter a keyword to generate instantly.', type: 'error' });
        setLoadingType('none'); return;
    }

    try {
      // Yeh request 10-15 seconds legi kyunki AI turant likh raha hai
      const response = await axios.post('https://www.projectinfotech.in/api/admin/blog-automation/generate-now', {
        keyword: firstKeyword
      });
      setMessage({ text: response.data.message, type: 'success' });
    } catch (error) {
      setMessage({ text: 'Error generating blog instantly. Check backend console.', type: 'error' });
    } finally {
      setLoadingType('none');
    }
  };

  return (
    <div className="animate-fade-in-up">
      <h2 className={`text-2xl font-bold mb-6 ${colors.navbarText}`}>AI Blog Automation Setup</h2>

      <div className={`p-8 rounded-xl shadow-sm border max-w-3xl ${colors.cardBg} ${colors.cardText} ${colors.borderColor}`}>
        <p className="mb-6 opacity-80">
          Enter your target SEO keywords. You can either schedule them across multiple days or generate the first keyword instantly right now.
        </p>

        {message && (
            <div className={`mb-6 p-4 rounded-lg text-sm font-bold ${message.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                {message.text}
            </div>
        )}

        <div className="space-y-6">
          <div>
            <label className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide opacity-70 mb-2">
              <FileText size={16} /> Enter Keywords (One per line)
            </label>
            <textarea 
              rows={8}
              value={keywords}
              onChange={(e) => setKeywords(e.target.value)}
              className={`w-full p-4 rounded-lg border focus:ring-2 focus:ring-blue-500 outline-none transition bg-transparent ${colors.borderColor}`}
              placeholder="react development roadmap&#10;what is spring boot&#10;mern vs mean..."
            />
          </div>

          <div>
            <label className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide opacity-70 mb-2">
              <Calendar size={16} /> Total Days (For Scheduled Auto-Pilot only)
            </label>
            <input 
              type="number"
              min="1"
              value={days}
              onChange={(e) => setDays(e.target.value)}
              className={`w-full p-4 rounded-lg border focus:ring-2 focus:ring-blue-500 outline-none transition bg-transparent ${colors.borderColor}`}
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-4 pt-4 border-t border-gray-200">
             {/* Instant Action Button */}
             <button 
              onClick={handleInstantGenerate}
              disabled={loadingType !== 'none'}
              className={`flex-1 flex items-center justify-center gap-2 font-bold py-4 rounded-lg transition transform active:scale-95 ${loadingType === 'instant' ? 'bg-purple-400 cursor-not-allowed' : 'bg-purple-600 hover:bg-purple-700 text-white shadow-lg shadow-purple-500/30'}`}
            >
              {loadingType === 'instant' ? <Loader2 className="animate-spin" size={20} /> : <Zap size={20} />}
              {loadingType === 'instant' ? 'Writing Blog (Wait 15s)...' : 'Generate Now (1st Keyword)'}
            </button>

            {/* Scheduled Action Button */}
            <button 
              onClick={handleStartAutomation}
              disabled={loadingType !== 'none'}
              className={`flex-1 flex items-center justify-center gap-2 font-bold py-4 rounded-lg transition transform active:scale-95 ${loadingType === 'scheduled' ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-500/30'}`}
            >
              {loadingType === 'scheduled' ? <Loader2 className="animate-spin" size={20} /> : <Play size={20} />}
              Schedule Auto-Pilot
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAiBlog;